import * as React from "react";
import { JSX } from "react";
import { toast } from "react-toastify";
import { Client } from "@app/client/client";
import { JobQueue, JobType, JobQueueService } from "@app/jobs";
import { Resource } from "@app/resources";
import ProjectService from "@app/projects/projectService";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import SingleSelectNestedDropdown from "@app/shared/checkbox-dropdown/SingleSelectNestedDropdown";
import SingleSelectDropdown from "@app/shared/checkbox-dropdown/SingleSelectDropdown";
import { DropdownItem, NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { CountyLite } from "@app/geography/county";
import { JobQueueMapListItem } from "@app/jobs/job-queue-mapping/jobQueueMapSummary";
import { JobQueueMapModel } from "@app/jobs/job-queue-mapping/JobQueueMapModel";
import { EnumerationItemModel } from "@app/shared/interfaces/enumerationItemModel";

interface JobQueueMapFlyoutProps {
  clients?: Client[];
  jobTypeItems?: EnumerationItemModel[];
  jobQueues: JobQueue[];
  counties: CountyLite[];
  countyNestedItems?: NestingParentDropdownItem[];
  onUpsert: (request: JobQueueMapModel) => Promise<void>;
  mode?: 'add' | 'edit';
  initialData?: JobQueueMapListItem;
}

const JobQueueMapFlyout = (props: JobQueueMapFlyoutProps): JSX.Element => {
  const projectService = React.useRef(new ProjectService()).current;
  const jobQueueService = React.useRef(new JobQueueService()).current;

  const clients = props.clients ?? [];
  const jobQueues = props.jobQueues ?? [];
  const jobTypes = props.jobTypeItems ?? [];
  const [projects, setProjects] = React.useState<Resource[]>([]);
  const [jobTypeItem, setJobTypeItem] = React.useState<EnumerationItemModel | null>(null);
  const jobType: JobType | null = jobTypeItem ? (jobTypeItem.value as unknown as JobType) : null;

  const [client, setClient] = React.useState<Client | null>(null);
  const [project, setProject] = React.useState<Resource | null>(null);
  const [countySSA, setCountySSA] = React.useState<string | null>( props.initialData?.countySSA ?? null );
  const [jobQueue, setJobQueue] = React.useState<JobQueue | null>(null);
  const [selectedCountyItem, setSelectedCountyItem] = React.useState<DropdownItem<string> | null>(null);

  const [createEnabled, setCreateEnabled] = React.useState<boolean>(false);
  const [validationError, setValidationError] = React.useState<string>("");
  const [isSaving, setIsSaving] = React.useState(false);
  const flyoutContext = React.useContext(FlyoutContext);

  const formatIdName = (id: number | string, name: string) => `${id} - ${name}`;

  const jobTypeItems = React.useMemo<DropdownItem<string>[]>(() => {
    const allOption = { key: "", name: "None" };

    const mapped = (props.jobTypeItems ?? []).map(jt => ({
      key: jt.id.toString(),
      name: formatIdName(jt.id, jt.value),
     
      sortName: jt.value.toLowerCase(),
    }));

    // Sort by the name, then by ID 
    mapped.sort((a, b) => {
      if (a.sortName < b.sortName) return -1;
      if (a.sortName > b.sortName) return 1;

      // sort by numeric ID
      return Number(a.key) - Number(b.key);
    });

    return [allOption, ...mapped];
  }, [props.jobTypeItems]);


  const clientItems = React.useMemo<DropdownItem<string>[]>(() => {
    const allOption = { key: "", name: "None" };

    const mapped = clients.map(c => ({
      key: c.clientId.toString(),
      name: formatIdName(c.clientId, c.name),
      sortName: c.name.toLowerCase(),
    }));

    mapped.sort((a, b) => {
      if (a.sortName < b.sortName) return -1;
      if (a.sortName > b.sortName) return 1;
      return Number(a.key) - Number(b.key);
    });

    return [allOption, ...mapped];
  }, [clients]);

  const projectItems = React.useMemo<DropdownItem<string>[]>(() => {
    const allOption = { key: "", name: "None" };

    const mapped = projects.map(p => ({
      key: p.resourceId.toString(),
      name: `${p.resourceId} - ${p.name.trim()}`,
      sortName: p.name.toLowerCase(),
    }));

    mapped.sort((a, b) => {
      if (a.sortName < b.sortName) return -1;
      if (a.sortName > b.sortName) return 1;
    
      return Number(a.key) - Number(b.key);
    });

    return [allOption, ...mapped];
  }, [projects]);

  const jobQueueItems = React.useMemo<DropdownItem<string>[]>(() => {
    const allOption = { key: "", name: "None" };

    const mapped = jobQueues.map(q => ({
      key: q.id.toString(),
      name: `${q.id} - ${q.name.trim()}`,
      sortName: q.name.toLowerCase(),
    }));

    mapped.sort((a, b) => {
      if (a.sortName < b.sortName) return -1;
      if (a.sortName > b.sortName) return 1;
     
      return Number(a.key) - Number(b.key);
    });

    return [allOption, ...mapped];
  }, [jobQueues]);

  const selectedJobTypeItem = React.useMemo(
    () =>
      jobTypeItem
        ? jobTypeItems.find(i => i.key === jobTypeItem.id.toString()) ?? jobTypeItems[0]
        : jobTypeItems[0],
    [jobTypeItems, jobTypeItem]
  );

  const selectedClientItem = React.useMemo(
    () => clientItems.find(i => i.key === client?.clientId?.toString()) ?? clientItems[0],
    [clientItems, client]
  );

  const selectedProjectItem = React.useMemo(
    () => projectItems.find(i => i.key === project?.resourceId?.toString()) ?? projectItems[0],
    [projectItems, project]
  );

  const selectedJobQueueItem = React.useMemo(
    () => jobQueueItems.find(i => i.key === jobQueue?.id?.toString()) ?? jobQueueItems[0],
    [jobQueueItems, jobQueue]
  );

  const handleJobTypeChange = React.useCallback((item: DropdownItem<string> | undefined) => {
    if (!item || item.key === "") {
      setJobTypeItem(null);
      return;
    }

    const id = Number(item.key);
    const selectedItem = jobTypes.find(jt => jt.id === id);
    setJobTypeItem(selectedItem ?? null);
  }, [jobTypes]); 

  const handleClientChange = React.useCallback((item: DropdownItem<string> | undefined) => {
    if (!item || item.key === "") {
      setClient(null);
      return;
    }
    const id = Number(item.key);
    setClient(clients.find(c => c.clientId === id) ?? null);
  }, [clients]);

  const handleProjectChange = React.useCallback((item: DropdownItem<string> | undefined) => {
    const id = item?.key ? Number(item.key) : null;
    setProject(id ? projects.find(p => p.resourceId === id) : null);
  }, [projects]);

  const handleJobQueueChange = React.useCallback((item: DropdownItem<string> | undefined) => {
    const id = item?.key ? Number(item.key) : null;
    setJobQueue(id ? jobQueues.find(q => q.id === id) : null);
  }, [jobQueues]);

  React.useEffect(() => {
    if (countySSA && !selectedCountyItem && props.countyNestedItems) {
      const found = props.countyNestedItems
        .flatMap(p => p.children)
        .find(c => String(c.key) === String(countySSA));

      if (found) {
        setSelectedCountyItem(found);
      }
    }
  }, [countySSA, props.countyNestedItems]);

  React.useEffect(() => {
    if (props.mode === 'edit' && props.initialData) {
      const data = props.initialData;

      const foundJobTypeItem = jobTypes.find(jt => jt.id === data.jobTypeId);
      setJobTypeItem(foundJobTypeItem ?? null);

      const foundClient = clients.find(c => c.clientId === data.clientId);
      setClient(foundClient || null);

      const foundQueue = jobQueues.find(q => q.id === data.jobQueueId);
      setJobQueue(foundQueue || null);

      setCountySSA(data.countySSA ?? null);
     
    } else {
      setJobTypeItem(null);
      setClient(null);
      setProject(null);
      setJobQueue(null);
      setCountySSA(null);
    }
  }, [props.mode, props.initialData, clients, jobQueues]);

  React.useEffect(() => {
    const loadProjects = async () => {
      if (!client || typeof client.clientId !== 'number') {
        setProjects([]);
        setProject(null);
        return;
      }

      try {
        const projs = await projectService.getClientProjectResources(client.clientId);
        setProjects(projs || []);

        if (props.mode === 'edit' && props.initialData?.projectId) {
          const found = projs.find(p => p.resourceId === props.initialData.projectId);
          setProject(found || null);
        }
      } catch {
        setProjects([]);
        setProject(null);
      }
    };

    loadProjects();
  }, [client?.clientId, props.mode, props.initialData]);

  React.useEffect(() => {
    if (!project) return;
    if (!client) {
      setProject(null);
      return;
    }
    const projectIsValid = projects.some(p => p.resourceId === project.resourceId);
    if (!projectIsValid) setProject(null);
  }, [client, projects, project]);

  React.useEffect(() => {
    const checkDuplicate = async () => {
      if (!jobQueue?.id) {
        setCreateEnabled(false);
        setValidationError("");
        return;
      }

      if (!jobTypeItem && !client && !project) {
        setCreateEnabled(false);
        setValidationError("At least one of Job Type, Client, or Project must be specified");
        return;
      }

      try {
        const exists = await jobQueueService.jobQueueMapExists({
          clientId: client?.clientId,
          countySSA: countySSA ?? undefined,
          jobQueueMapId: props.mode === 'edit' && props.initialData
            ? props.initialData.jobQueueMapId
            : undefined,
          jobType: jobType ?? undefined,
          projectId: project?.resourceId,
        });

        if (exists) {
          setValidationError("A Job Queue Map already exists with this combination");
          setCreateEnabled(false);
        } else {
          setValidationError("");
          setCreateEnabled(true);
        }
      } catch (err: unknown) {
        setValidationError("Error checking for duplicates");
        setCreateEnabled(false);
      }
    };

    const timer = setTimeout(checkDuplicate, 300);
    return () => clearTimeout(timer);
  }, [
    jobTypeItem?.id,
    client?.clientId,
    project?.resourceId,
    countySSA,
    jobQueue?.id,
    props.mode,
    props.initialData?.jobQueueMapId,
  ]);

  React.useEffect(() => {
    setValidationError("");
  }, [jobTypeItem?.id, client?.clientId, project?.resourceId, jobQueue?.id, countySSA]);

  const handleSubmit = async () => {
    if (!jobQueue?.id || isSaving) return;

    setIsSaving(true);
    setValidationError("");

    const idForUpsert = props.mode === 'edit' && props.initialData
      ? props.initialData.jobQueueMapId
      : undefined;

    try {
      await props.onUpsert({
        clientId: client?.clientId,
        countySSA: countySSA ?? undefined,
        jobQueueId: jobQueue.id,
        jobQueueMapId: idForUpsert,
        jobType: jobType ?? undefined,
        projectId: project?.resourceId,
      });

      flyoutContext.handleCloseFlyout();
    } catch (err: unknown) {
      let message = "Failed to save Job Queue Map. Please check your input and try again.";

      if (err && typeof err === "object" && err !== null) {
        if ("response" in err) {
          const response = (err as { response?: unknown }).response;

          if (response && typeof response === "object" && response !== null) {
            if ("data" in response) {
              const data = (response as { data?: unknown }).data;

              if (typeof data === "string") {
                message = data;
              } else if (data && typeof data === "object" && "message" in data) {
                const dataMessage = (data as { message?: unknown }).message;
                if (typeof dataMessage === "string") {
                  message = dataMessage;
                }
              }
            }
          }
        } else if (err instanceof Error) {
          message = err.message;
        }
      }

      setValidationError(message);
      toast.error(message, {
        closeOnClick: true,
        containerId: "z-index-9999",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const buttonText = props.mode === 'edit' ? "Update" : "Create";

  const validationDisplay = validationError ? (
    <div className="row">
      <div className="col pl20 pr20 pt10">
        <QESAlert type="warning">{validationError}</QESAlert>
      </div>
    </div>
  ) : null;

  return (
    <>
      <div className="col">
        <div className="row mb5">
          <div className="col">

            <SingleSelectDropdown<string>
              label="Job Type"
              items={jobTypeItems}
              selectedItem={selectedJobTypeItem}
              onSelectionChanged={handleJobTypeChange}
              testId="job-queue-map-flyout_jobType"
              containerClassName="col-12 p-0 mb-3"
              required={false}
            />

            <SingleSelectDropdown<string>
              label="Client"
              items={clientItems}
              selectedItem={selectedClientItem}
              onSelectionChanged={handleClientChange}
              testId="job-queue-map-flyout_client"
              containerClassName="col-12 p-0 mb-3"
              required={false}
            />

            <SingleSelectDropdown<string>
              label="Project"
              items={projectItems}
              selectedItem={selectedProjectItem}
              onSelectionChanged={handleProjectChange}
              testId="job-queue-map-flyout_project"
              containerClassName="col-12 p-0 mb-3"
              disabled={!client}
              required={false}
            />

            <SingleSelectDropdown<string>
              label="Job Queue"
              items={jobQueueItems}
              selectedItem={selectedJobQueueItem}
              onSelectionChanged={handleJobQueueChange}
              testId="job-queue-map-flyout_jobQueue"
              containerClassName="col-12 p-0 mb-3"
              required={true}
            />

            <SingleSelectNestedDropdown
              label="County"
              testId="job-queue-map-flyout_county"
              containerClassName="col-12 p-0"
              items={props.countyNestedItems ?? []}
              selectedItem={selectedCountyItem ?? undefined}
              customSelectInputText={selectedCountyItem?.name ?? (countySSA || "None")}
              onSelectionChanged={(item) => {
                setSelectedCountyItem(item ?? null);
                setCountySSA(item?.key ?? null);
              }}
            />

          </div>
        </div>
      </div>

      {validationDisplay}

      <FlyoutFooter
        submitButton={
          <QESButton
            qesTestId="job-queue-map-flyout_submit"
            qesType="primary"
            className="mb-3"
            disabled={!createEnabled || !jobQueue?.id || isSaving}
            onClick={handleSubmit}
          >
            {isSaving ? "Saving..." : buttonText}
          </QESButton>
        }
      />
    </>
  );
};

export default JobQueueMapFlyout;